import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import Api from '@plone/volto/helpers/Api/Api';
import mime from 'mime-types';
import config from '@plone/volto/registry';
import { MEDIA_PAGE_WOWZA_ROUTE } from 'volto-base-addon/constants';
import { buildSubtitleLabel } from 'volto-base-addon/helpers/subtitleLabel';
import { withPathKeyParam } from 'volto-base-addon/helpers/pathKey';
import { outputStateOf } from 'volto-base-addon/helpers/mediaProcessingLogic';

// Build the player's subtitle track list from the (enriched) child items.
// Only completed subtitles are included — read from the output_state metadata
// carried on each item, so no per-item @workflow request is needed.
const subtitlesFromItems = (items, intl) => {
  const subtitles = [];
  for (const item of items) {
    if (item['@type'] !== 'subtitle') {
      continue;
    }
    if (outputStateOf(item) !== 'completed') {
      continue;
    }
    subtitles.push([
      {
        '@id': item['url'] || new URL(item['@id'])?.pathname,
        title: item['title'],
        label: buildSubtitleLabel(item),
      },
    ]);
  }
  return subtitles;
};

export const getVideoPlayerData = async (
  items,
  objectId,
  live_stream_url,
  uid,
  token,
  api,
  signal = null,
  contentPath = null,
  intl = null,
  pathKey = null,
) => {
  const tempData = {};
  if (items?.length) {
    const subtitles = subtitlesFromItems(items, intl);
    if (subtitles.length) {
      tempData.subtitles = subtitles;
    }
  }

  if (objectId !== null && objectId !== '') {
    const fetchOptions = {};
    if (signal) {
      fetchOptions.signal = signal;
    }

    let streamUrl = null;

    // Use backend @stream-url endpoint if content path is available
    if (contentPath) {
      const resp = await api.get(
        withPathKeyParam(contentPath + '/@stream-url', pathKey),
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
          signal: signal,
        },
      );
      streamUrl = resp?.stream_url;
    } else {
      // Fallback to Express middleware route (upload flow, no Plone content yet)
      // The route gates on @actions, which is pathkey-protected too, so the key
      // has to travel with it or a protected embed gets no stream.
      const resp = await fetch(
        withPathKeyParam(
          `${config.settings.publicPath}/${MEDIA_PAGE_WOWZA_ROUTE}/${uid}/${objectId}`,
          pathKey,
        ),
        fetchOptions,
      );
      if (!resp.ok) {
        throw new Error(`Failed to fetch stream URL: ${resp.statusText}`);
      }
      const jsonData = await resp.json();
      streamUrl = jsonData?.url;
    }

    if (streamUrl) {
      tempData.sources = [
        {
          src: streamUrl,
          type: mime.lookup(streamUrl) || '',
        },
      ];
    } else {
      // eslint-disable-next-line no-console
      console.warn('Stream URL missing in response for objectId:', objectId);
    }
  } else if (live_stream_url) {
    tempData.sources = [
      {
        src: live_stream_url,
        type: mime.lookup(live_stream_url) || '',
      },
    ];
  }

  if (!tempData.sources) {
    tempData.sources = [];
  }

  return tempData;
};

export const useVideoPlayerData = (
  items = [],
  objectId,
  live_stream_url,
  uid,
  onFail = null,
  contentPath = null,
) => {
  const { token } = useSelector((state) => state.userSession);
  const intl = useIntl();
  const [data, setData] = useState({ isReady: false, data: {} });

  useEffect(() => {
    const currentItems = Array.isArray(items) ? items : [];

    if (!uid || (!objectId && !live_stream_url)) {
      setData({ isReady: true, data: {} });
      return;
    }

    const controller = new AbortController();
    const api = new Api();
    let isMounted = true;

    getVideoPlayerData(
      currentItems,
      objectId,
      live_stream_url,
      uid,
      token,
      api,
      controller.signal,
      contentPath,
      intl,
    )
      .then((result) => {
        if (isMounted) {
          setData({ isReady: true, data: result });
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Error fetching video player data:', err);
          if (isMounted) {
            setData({ isReady: true, data: {} });
            if (onFail) {
              onFail(err);
            }
          }
        }
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return data;
};
